import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AgentService } from '../../services/agent.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  AgentFileDirectoriesService,
  AgentFilesMediaService,
  AgentImagesService,
  ChangeSourceTypeService,
} from '../../../public-api';
import { BaseModelKeys } from '../../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/base.model';
import {
  AgentFileDirectory,
  AgentFileDirectoryKeys,
  AgentMedia,
  AgentMediaKeys,
  AGMedia,
  ChangeSourceType,
} from '@ag-common-lib/public-api';
import { distinctUntilChanged, shareReplay, switchMap } from 'rxjs/operators';

@Injectable()
export class AgentMediaComponentService {
  private changeSourceTypeService = inject(ChangeSourceTypeService);
  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  private _agentId$ = new BehaviorSubject<string>(null);
  agentId$ = this._agentId$.asObservable();

  private _currentDirectory$ = new BehaviorSubject<AgentFileDirectory>(null);
  currentDirectory$ = this._currentDirectory$.asObservable();

  currentMediaList$: Observable<AgentMedia[]>;

  constructor(
    private agentFilesMediaService: AgentFilesMediaService,
    private agentImagesService: AgentImagesService,
    private agentFileDirectoriesService: AgentFileDirectoriesService,
    private agentService: AgentService,
    private toastrService: ToastrService,
  ) {
    this.currentMediaList$ = combineLatest([this._agentId$.pipe(distinctUntilChanged()), this._currentDirectory$]).pipe(
      switchMap(([agentId, currentDirectory]) => {
        const directoryId = currentDirectory?.[BaseModelKeys.dbId];
        if (!agentId || !directoryId) {
          return of([]);
        }

        // Guard against mismatched agent/directory from a previous profile
        const directoryAgentId = currentDirectory?.[AgentFileDirectoryKeys.agentDbId];
        if (directoryAgentId && directoryAgentId !== agentId) {
          return of([]);
        }

        return this.agentFilesMediaService.getList(agentId, directoryId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  get currentDirectory(): AgentFileDirectory {
    return this._currentDirectory$.value as AgentFileDirectory;
  }

  setAgentId(agentId: string): void {
    if (this._agentId$.value === agentId) {
      return;
    }

    // Clear directory immediately so shareReplay cannot keep previous agent media
    this._currentDirectory$.next(null);
    this._agentId$.next(agentId);
  }

  setCurrentDirectory(directory: AgentFileDirectory): void {
    const agentId = this._agentId$.value;
    const directoryAgentId = directory?.[AgentFileDirectoryKeys.agentDbId];

    if (directory && agentId && directoryAgentId && directoryAgentId !== agentId) {
      return;
    }

    this._currentDirectory$.next(directory);
  }

  setAsPrimaryImage(agentId: string, media: AGMedia) {
    // // todo update agent profile picture to use wasabi
    // this.agentService
    //   .updateAgentFields(this.agentId$.value, {
    //     [AgentKeys.p_headshot_link]: media[AGMediaKeys.wasabiPath],
    //   })
    //   .then(() => {
    //     this.toastrService.error('Profile picture is updated!');
    //   });
  }

  deleteMedia(agentId: string, media: AGMedia, action: ChangeSourceType | null = null): Promise<boolean> {
    return this.agentFilesMediaService
      .delete(agentId, this.currentDirectory[BaseModelKeys.dbId], media[BaseModelKeys.dbId], action ?? this._sourceType)
      .then(() => false)
      .catch(err => {
        this.toastrService.error('Media is not deleted!');
        throw err;
      })
      .finally(() => {});
  }

  createMedia(agentId: string, media: AGMedia, action: ChangeSourceType | null = null): Promise<AgentMedia | void> {
    if (this.currentDirectory?.[BaseModelKeys.dbId]) {
      return this.agentFilesMediaService
        .create(
          agentId,
          this.currentDirectory[BaseModelKeys.dbId],
          Object.assign(media, { [AgentMediaKeys.agentDbId]: agentId }) as AgentMedia,
          true,
          action ?? this._sourceType
        )
        .catch(err => {
          this.toastrService.error('Media is not created!');
          throw err;
        })
        .finally(() => {});
    }

    return this.agentImagesService.addAgentMediaToImagesDirectory(agentId, {
      ...media,
      [AgentMediaKeys.agentDbId]: agentId,
    } as AgentMedia);
  }

  createFolder(directory: AgentFileDirectory): Promise<boolean> {
    const agentId = directory[AgentFileDirectoryKeys.agentDbId];
    return this.agentFileDirectoriesService
      .create(agentId, Object.assign(directory, { [AgentMediaKeys.agentDbId]: agentId }) as AgentFileDirectory)
      .then(() => false)
      .catch(err => {
        this.toastrService.error('Folder is not created!');
        throw err;
      })
      .finally(() => {});
  }

  deleteFolder(agentId: string, documentId: string): Promise<boolean> {
    return this.agentFileDirectoriesService
      .delete(agentId, documentId)
      .then(() => false)
      .catch(err => {
        this.toastrService.error('Folder is not removed!');
        throw err;
      })
      .finally(() => {});
  }
}
